import * as React from "react";
import { Link } from "react-router-dom";
import { Banner, Button, Card, ListStates, Table, Tbody, Td, Textarea, Th, Thead, Tr } from "../components";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { useStudentNames } from "../students/StudentDirectory";
import * as Store from "./MarkGridStore";

/**
 * The subject comments for one term: a row per student and a column per comment type, side by side. A comment belongs
 * to the term on screen (its qualification module), so the term switcher above moves between terms' comments.
 *
 * Edits stay local until Save, which sends only the comments that changed; a blank comment clears it. Comments, like
 * marks, can only be written for students actively enrolled in the term.
 */
export const SubjectComments: React.FC<{ moduleID: string; classID?: string; termName?: string }> = ({ moduleID, classID, termName }) => {
    const toast = useToast();
    const canWrite = usePermission(Permissions.MARKS_WRITE);
    const canConfigure = usePermission(Permissions.CONFIGURATION_WRITE);
    const comments = useAsync(() => Store.getComments(moduleID, classID), [moduleID, classID]);
    const [edits, setEdits] = React.useState<Record<string, string>>({});

    const rows = comments.data?.rows ?? [];
    const types = comments.data?.commentTypes ?? [];
    const maxLength = comments.data?.maxLength ?? 2000;
    const names = useStudentNames(rows.map((r) => r.studentID ?? ""));

    const key = (studentID: string, typeID: string) => `${studentID}|${typeID}`;
    const stored = (studentID: string, typeID: string) => rows.find((r) => r.studentID === studentID)?.comments?.[typeID] ?? "";
    const value = (studentID: string, typeID: string) => edits[key(studentID, typeID)] ?? stored(studentID, typeID);
    const changed = Object.entries(edits).filter(([k, v]) => {
        const [studentID, typeID] = k.split("|");
        return v.trim() !== stored(studentID, typeID).trim();
    });
    const tooLong = changed.some(([, v]) => v.trim().length > maxLength);

    const save = useAction(async () => {
        const byType = new Map<string, Array<{ studentID: string; result: string }>>();
        changed.forEach(([k, v]) => {
            const [studentID, typeID] = k.split("|");
            byType.set(typeID, [...(byType.get(typeID) ?? []), { studentID, result: v }]);
        });
        const result = await Store.saveComments(
            moduleID,
            Array.from(byType, ([commentTypeID, entries]) => ({ commentTypeID, entries })),
            classID
        );
        toast.success(`Saved ${result.savedComments} comment${result.savedComments === 1 ? "" : "s"}.`);
        setEdits({});
        comments.refresh();
    });

    const sorted = [...rows].sort((a, b) => names(a.studentID ?? "").localeCompare(names(b.studentID ?? "")));

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-text-strong">Subject comments{termName ? ` · ${termName}` : ""}</h2>
                    <p className="text-sm text-text-weak">
                        One comment per student for each comment type, for this term only. Leave a comment blank to remove it.
                    </p>
                </div>
                {canWrite && types.length > 0 && (
                    <Button icon={["fad", "floppy-disk"]} onClick={save.run} loading={save.busy} disabled={changed.length === 0 || tooLong}>
                        {changed.length ? `Save ${changed.length} comment${changed.length === 1 ? "" : "s"}` : "Save"}
                    </Button>
                )}
            </div>

            {save.error && (
                <Banner tone="danger" className="mb-4">
                    {save.error}
                </Banner>
            )}
            {changed.length > 0 && (
                <Banner tone="info" className="mb-4">
                    Unsaved comments stay on this term. Save them before switching term or class.
                </Banner>
            )}

            <Card>
                <ListStates
                    isLoading={comments.isLoading}
                    error={comments.error}
                    onRetry={comments.refresh}
                    isEmpty={types.length === 0 || rows.length === 0}
                    empty={
                        types.length === 0
                            ? {
                                  icon: ["fad", "comment"],
                                  title: "No comment types yet",
                                  hint: canConfigure ? (
                                      <>
                                          Add one (for example &ldquo;Subject comment&rdquo;) under{" "}
                                          <Link to="/configuration" className="text-primary-text underline">
                                              Configuration
                                          </Link>
                                          , on the Comment types tab.
                                      </>
                                  ) : (
                                      "Ask an administrator to add a comment type, such as “Subject comment”, under Configuration."
                                  ),
                              }
                            : { icon: ["fad", "users"], title: "No students in this term", hint: "Students appear once they are enrolled in this term of the subject." }
                    }
                >
                    <Table>
                        <Thead>
                            <tr>
                                <Th>Student</Th>
                                {types.map((t) => (
                                    <Th key={t.id}>{t.name}</Th>
                                ))}
                            </tr>
                        </Thead>
                        <Tbody>
                            {sorted.map((row) => {
                                const studentID = row.studentID ?? "";
                                return (
                                    <Tr key={studentID}>
                                        <Td className="align-top">
                                            <div className="font-medium text-text-strong">{names(studentID)}</div>
                                            {names(studentID) !== studentID && <div className="text-xs text-text-weak">{studentID}</div>}
                                        </Td>
                                        {types.map((t) => {
                                            const typeID = t.id ?? "";
                                            const text = value(studentID, typeID);
                                            const dirty = text.trim() !== stored(studentID, typeID).trim();
                                            const over = text.trim().length > maxLength;
                                            return (
                                                <Td key={typeID} className="min-w-[16rem] align-top">
                                                    <Textarea
                                                        rows={2}
                                                        aria-label={`${t.name} for ${names(studentID)}`}
                                                        value={text}
                                                        readOnly={!canWrite}
                                                        error={over ? `At most ${maxLength} characters.` : undefined}
                                                        className={dirty ? "bg-primary-soft" : ""}
                                                        onChange={(e) => setEdits((current) => ({ ...current, [key(studentID, typeID)]: e.target.value }))}
                                                    />
                                                    {(dirty || text.length > maxLength * 0.8) && (
                                                        <div className={`mt-1 text-right text-xs ${over ? "text-danger-text" : "text-text-weak"}`}>
                                                            {text.trim().length}/{maxLength}
                                                        </div>
                                                    )}
                                                </Td>
                                            );
                                        })}
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </ListStates>
            </Card>
            {!canWrite && types.length > 0 && rows.length > 0 && (
                <p className="mt-2 text-xs text-text-weak">You can read these comments but not change them.</p>
            )}
        </div>
    );
};

export default SubjectComments;
